/** Dirección normalizada — Argentina-first, compatible con couriers y Google Places. */

export interface NormalizedAddress {
  street: string;
  streetNumber: string;
  floor?: string;
  apartment?: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  formattedAddress: string;
  latitude?: number | null;
  longitude?: number | null;
  placeId?: string | null;
  label?: string | null;
  notes?: string | null;
}

export type NormalizedAddressInput = Partial<NormalizedAddress> & {
  street?: string;
  streetNumber?: string;
};

export const EMPTY_NORMALIZED_ADDRESS: NormalizedAddress = {
  street: "",
  streetNumber: "",
  floor: "",
  apartment: "",
  city: "",
  province: "",
  postalCode: "",
  country: "AR",
  formattedAddress: "",
  latitude: null,
  longitude: null,
  placeId: null,
  label: "",
  notes: "",
};

export function formatNormalizedAddressLine(a: Pick<NormalizedAddress, "street" | "streetNumber" | "floor" | "apartment" | "city" | "province" | "postalCode">): string {
  const parts: string[] = [];
  const streetLine = [a.street?.trim(), a.streetNumber?.trim()].filter(Boolean).join(" ");
  if (streetLine) parts.push(streetLine);
  const unit: string[] = [];
  if (a.floor?.trim()) unit.push(`Piso ${a.floor.trim()}`);
  if (a.apartment?.trim()) unit.push(`Dpto ${a.apartment.trim()}`);
  if (unit.length) parts.push(unit.join(" "));
  const locality = [a.postalCode?.trim(), a.city?.trim(), a.province?.trim()].filter(Boolean).join(" ");
  if (locality) parts.push(locality);
  return parts.join(", ");
}

export function buildFormattedAddress(input: NormalizedAddressInput): string {
  const trimmed = input.formattedAddress?.trim();
  if (trimmed) return trimmed;
  return formatNormalizedAddressLine({
    street: input.street ?? "",
    streetNumber: input.streetNumber ?? "",
    floor: input.floor,
    apartment: input.apartment,
    city: input.city ?? "",
    province: input.province ?? "",
    postalCode: input.postalCode ?? "",
  });
}


/** CPA argentino (C1184AAB) o basura de Places → 4 dígitos. */
export function normalizeArPostalCode(raw: string | null | undefined): string {
  const digits = String(raw ?? "").replace(/\D/g, "");
  if (digits.length >= 4) return digits.slice(0, 4);
  return digits;
}

export type ValidateNormalizedAddressOptions = {
  /** Obliga selección desde Google Places (placeId + coordenadas). */
  requireGeocoded?: boolean;
};

export function validateNormalizedAddress(
  input: NormalizedAddressInput,
  options?: ValidateNormalizedAddressOptions,
): string | null {
  if (!input.street?.trim()) return "La calle es obligatoria";
  if (!input.streetNumber?.trim()) return "El número es obligatorio";
  if (!input.city?.trim()) return "La localidad es obligatoria";
  if (!input.province?.trim()) return "La provincia es obligatoria";
  const postal = normalizeArPostalCode(input.postalCode);
  if (!postal) return "El código postal es obligatorio";
  if (!/^\d{4}$/.test(postal)) return "El código postal debe tener 4 dígitos";
  if (options?.requireGeocoded) {
    if (!input.placeId?.trim()) {
      return "Elegí la dirección desde las sugerencias de Google Maps";
    }
    if (input.latitude == null || input.longitude == null || Number.isNaN(Number(input.latitude)) || Number.isNaN(Number(input.longitude))) {
      return "La dirección debe tener ubicación en el mapa. Buscala de nuevo en Google Maps";
    }
  }
  return null;
}

export function normalizedAddressFromDb(row: Record<string, unknown>): NormalizedAddress {
  return {
    street: String(row.street ?? ""),
    streetNumber: String(row.street_number ?? row.streetNumber ?? ""),
    floor: (row.floor as string | null) ?? "",
    apartment: (row.apartment as string | null) ?? "",
    city: String(row.city ?? ""),
    province: String(row.province ?? ""),
    postalCode: normalizeArPostalCode(String(row.postal_code ?? row.postalCode ?? "")),
    country: String(row.country ?? "AR"),
    formattedAddress: String(row.address ?? row.formatted_address ?? ""),
    latitude: row.latitude != null ? Number(row.latitude) : null,
    longitude: row.longitude != null ? Number(row.longitude) : null,
    placeId: (row.place_id as string | null) ?? null,
    label: (row.label as string | null) ?? null,
    notes: (row.notes as string | null) ?? null,
  };
}
